import { useMemo, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import bakedData from "@/data/emailZeroBoard.json";
import { useBoard } from "@/hooks/useBoard";
import { EmailZeroBoard } from "./EmailZeroBoard";
import { Card } from "@/design/primitives";

// The zero-inbox wall at /email/board — the count Jeff trusts.
//
// Live from the "email-zero" board when the feed answers; the last committed
// window when it does not. A source that cannot be read fails loud (the
// board says so) — it never renders as a quiet empty wall.

// Nothing Jeff reads may carry a raw contact string. The wall's own copy is
// checked here so a wording slip can never ship a customer address.
//
// The banned-term list and the privacy patterns live in the differential gate
// (ci/email-zero-differential.py) — the one place that checks them — rather
// than here, so this file never spells a term the board's wording gate bans.
const PRIVACY_PATTERNS = [
  /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/,
  /(?<!\d)(?:\+?\d{1,3}[\s.\-]?)?\(?\d{3}\)?[\s.\-]?\d{3}[\s.\-]?\d{4}(?!\d)/,
  /\/Users\/|\.hermes\/|\/Volumes\//,
];

// Our own published business lines are allowed in our own outgoing subjects.
const BIZ_PHONE = /(?:\(?\s?844\s?\)?[\s.\-]?423[\s.\-]?9696)|(?:\(?\s?435\s?\)?[\s.\-]?222[\s.\-]?(?:4669|2188))|(?:\(?\s?800\s?\)?[\s.\-]?903[\s.\-]?0274)/g;

const cleanText = (s) => s.replace(BIZ_PHONE, " ");

const findLeak = (node, path = "") => {
  if (typeof node === "string") {
    const probe = cleanText(node);
    if (PRIVACY_PATTERNS.some((p) => p.test(probe))) return `${path}: ${node.slice(0, 80)}`;
    return null;
  }
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i += 1) {
      const hit = findLeak(node[i], `${path}[${i}]`);
      if (hit) return hit;
    }
    return null;
  }
  if (node && typeof node === "object") {
    for (const [k, v] of Object.entries(node)) {
      const hit = findLeak(v, path ? `${path}.${k}` : k);
      if (hit) return hit;
    }
  }
  return null;
};

export default function EmailZeroWallPage() {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const board = useBoard("email-zero");
  const [dismissed, setDismissed] = useState(false);

  const liveCards = useMemo(() => board.items || [], [board.items]);

  // The live stamp carries the counts; the baked stamp is the fallback.
  const stamp = useMemo(() => {
    if (board.live && board.meta && board.meta.clean_total != null) return board.meta;
    return bakedData.stamp || {};
  }, [board.live, board.meta]);

  const cards = useMemo(() => {
    const source = board.live && liveCards.length ? liveCards : (bakedData.cards || []);
    return source.map((c) => ({ ...c, payload: c.payload || {} }));
  }, [board.live, liveCards]);

  // A source that cannot be read fails loud: the wall says the feed is not
  // answering rather than showing an empty board.
  const unreadable = board.status === "down" && !board.live;
  const leak = useMemo(() => findLeak({ stamp, cards }), [stamp, cards]);

  return (
    <div data-testid="email-zero-page" className="w-full font-inter text-os-ink">
      <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
        {unreadable && (
          <Card quiet className="border-os-red px-5 py-3" data-testid="email-zero-unreadable">
            <p className="text-[0.875rem] leading-[1.45] text-os-red">
              Kit&apos;s mail counts can&apos;t be read right now — the wall is showing the last
              committed numbers.
            </p>
          </Card>
        )}
        {leak && (
          <Card quiet className="border-os-red px-5 py-3" data-testid="email-zero-privacy-warning">
            <p className="text-[0.875rem] leading-[1.45] text-os-red">
              Some details on this wall are not safe to show — Kit is fixing that.
            </p>
          </Card>
        )}
        {board.live && !dismissed && !stamp.ingested_at && (
          <button
            type="button"
            data-testid="email-zero-sample-note"
            onClick={() => setDismissed(true)}
            className="self-start rounded-control border border-os-border px-3 py-1.5 text-left font-mono text-[0.6875rem] text-os-ink-3 hover:text-os-ink"
          >
            Today these numbers are a hand-built sample — Kit&apos;s live mail counts arrive on the
            first fetch. Tap to dismiss.
          </button>
        )}
        <EmailZeroBoard cards={cards} stamp={stamp} variant={desktop ? "desktop" : "mobile"} />
        {!board.live && !unreadable && (
          <Card quiet className="px-5 py-3">
            <p className="text-[0.875rem] leading-[1.45] text-os-ink-2">
              Kit&apos;s mail counts are offline this visit — the page shows the last committed state.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}
